import React, { useState } from "react";
import { Building2Icon, PencilIcon, PlusIcon } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetBillingDetailsQuery, usePostFileMutation, useUpdateBillingDetailsMutation } from "@/store/rest_api";
import { BillingDetails as BillingDetailsData } from "@/model/interface/Organisation";
import FileResponse from "@/model/interface/FileResponse";
import { BILLING_STATE_CODES, formatInvoiceDate } from "@/util/invoice";
import { apiErrorMessage } from "@/util/apiError";

const EMPTY_DETAILS: BillingDetailsData = {
  billingName: "",
  billingAddressLine1: "",
  billingAddressLine2: "",
  billingCity: "",
  billingState: "",
  billingPostalCode: "",
  billingCountry: "US",
  billingEin: "",
  taxStatus: "TAXABLE",
  taxExemption: null,
};

function salesTaxSummary(details?: BillingDetailsData): string {
  const exemption = details?.taxExemption;
  if (details?.taxStatus === "EXEMPT") {
    return exemption?.expiresOn ? `Sales tax: exempt until ${formatInvoiceDate(exemption.expiresOn)}` : "Sales tax: exempt";
  }
  if (exemption?.status === "PENDING_REVIEW") {
    return "Sales tax: taxable, exemption pending review";
  }
  if (exemption?.status === "REJECTED") {
    return "Sales tax: taxable, exemption not approved";
  }
  return "Sales tax: taxable";
}

export function BillingDetails() {
  const { data, isLoading } = useGetBillingDetailsQuery();
  const [updateBillingDetails, { isLoading: isSaving }] = useUpdateBillingDetailsMutation();
  const [uploadFile, { isLoading: isUploading }] = usePostFileMutation();
  const [details, setDetails] = useState<BillingDetailsData>(EMPTY_DETAILS);
  const [uploadedCertificate, setUploadedCertificate] = useState<FileResponse | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const hasAddress = !!data?.billingAddressLine1?.trim();
  const streetLine = [data?.billingAddressLine1, data?.billingAddressLine2].filter(Boolean).join(", ");
  const cityLine = [data?.billingCity, [data?.billingState, data?.billingPostalCode].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  const exemption = data?.taxExemption;
  const exemptionOpen = exemption?.status === "PENDING_REVIEW" || exemption?.status === "APPROVED";
  const certificateHint = uploadedCertificate
    ? `New certificate: ${uploadedCertificate.name ?? "certificate"}. We review it before your invoices stop charging sales tax.`
    : exemptionOpen && exemption?.certificate
      ? `On file: ${exemption.certificate.name ?? "certificate"}, ${exemption.status === "APPROVED" ? "approved" : "pending review"}.`
      : "Upload the exemption certificate for your state. We review it before your invoices stop charging sales tax.";

  const setField = (field: keyof BillingDetailsData, value: string) => {
    setDetails((current) => ({ ...current, [field]: value }));
  };

  const handleCertificateChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      const result = await uploadFile({ file }).unwrap();
      const uploaded = (Array.isArray(result) ? result[0] : result) as FileResponse | undefined;
      if (!uploaded?.id) {
        toast.error("Could not upload the certificate");
        return;
      }
      setUploadedCertificate(uploaded);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not upload the certificate"));
    }
  };

  const handleSave = async () => {
    try {
      await updateBillingDetails({
        ...details,
        billingCountry: "US",
        taxExemption: uploadedCertificate ? { certificate: { id: uploadedCertificate.id } } : null,
      }).unwrap();
      toast.success("Billing details saved");
      setIsEditing(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not save the billing details"));
    }
  };

  const openEditor = () => {
    setDetails(
      data
        ? { ...EMPTY_DETAILS, ...data, taxStatus: data.taxStatus === "EXEMPT" || exemptionOpen ? "EXEMPT" : "TAXABLE" }
        : EMPTY_DETAILS,
    );
    setUploadedCertificate(null);
    setIsEditing(true);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="space-y-3 p-6">
          {[1, 2, 3].map((row) => (
            <Skeleton key={row} className="h-6 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2">
            <Building2Icon className="h-5 w-5" />
            Billing details
          </CardTitle>
          {hasAddress && (
            <Button variant="outline" size="sm" onClick={openEditor}>
              <PencilIcon className="mr-2 h-4 w-4" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {hasAddress ? (
            <>
              <div className="text-sm">
                {data?.billingName && <p className="font-semibold">{data.billingName}</p>}
                <p>{streetLine}</p>
                <p>{cityLine}</p>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {data?.billingEin && <span>EIN {data.billingEin}</span>}
                <span>{salesTaxSummary(data)}</span>
              </div>
              {exemption?.status === "REJECTED" && exemption.rejectionReason && (
                <p className="text-xs text-muted-foreground">{exemption.rejectionReason}</p>
              )}
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                No billing address yet. Your invoices and sales tax use it, and choosing a plan needs it.
              </p>
              <Button variant="outline" size="sm" onClick={openEditor}>
                <PlusIcon className="mr-2 h-4 w-4" />
                Add billing address
              </Button>
            </>
          )}
        </CardContent>
      </Card>
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="max-h-[90vh] overflow-y-auto text-foreground">
          <DialogHeader>
            <DialogTitle>Billing details</DialogTitle>
            <DialogDescription>The name and address on your invoices. Sales tax is calculated from this address.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="billing-name">Legal business name</Label>
            <Input
              id="billing-name"
              value={details.billingName ?? ""}
              onChange={(event) => setField("billingName", event.target.value)}
              placeholder="Harbor Street Homes LLC"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="billing-line1">Street address</Label>
            <Input
              id="billing-line1"
              value={details.billingAddressLine1 ?? ""}
              onChange={(event) => setField("billingAddressLine1", event.target.value)}
              placeholder="1200 Main St"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="billing-line2">Suite or unit (optional)</Label>
            <Input
              id="billing-line2"
              value={details.billingAddressLine2 ?? ""}
              onChange={(event) => setField("billingAddressLine2", event.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="billing-city">City</Label>
              <Input
                id="billing-city"
                value={details.billingCity ?? ""}
                onChange={(event) => setField("billingCity", event.target.value)}
                placeholder="Houston"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="billing-state">State</Label>
              <Select value={details.billingState ?? ""} onValueChange={(value) => setField("billingState", value)}>
                <SelectTrigger id="billing-state">
                  <SelectValue placeholder="Choose a state" />
                </SelectTrigger>
                <SelectContent>
                  {BILLING_STATE_CODES.map((stateCode) => (
                    <SelectItem key={stateCode} value={stateCode}>
                      {stateCode}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="billing-postal">ZIP code</Label>
              <Input
                id="billing-postal"
                value={details.billingPostalCode ?? ""}
                onChange={(event) => setField("billingPostalCode", event.target.value)}
                placeholder="77002"
                inputMode="numeric"
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="billing-ein">EIN (optional)</Label>
              <Input
                id="billing-ein"
                value={details.billingEin ?? ""}
                onChange={(event) => setField("billingEin", event.target.value)}
                placeholder="12-3456789"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="billing-tax-status">Sales tax</Label>
              <Select value={details.taxStatus} onValueChange={(value) => setField("taxStatus", value)}>
                <SelectTrigger id="billing-tax-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TAXABLE">Charge sales tax</SelectItem>
                  <SelectItem value="EXEMPT">Tax exempt</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {details.taxStatus === "EXEMPT" && (
            <div className="space-y-2">
              <Label htmlFor="billing-certificate">Exemption certificate</Label>
              <Input id="billing-certificate" type="file" accept="application/pdf,image/*" onChange={handleCertificateChange} />
              <p className="text-xs text-muted-foreground">{certificateHint}</p>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving || isUploading}>
              Save billing details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
